import { useState, useEffect, useRef, useCallback } from 'react';
import { GeopositSuggestion } from '@/lib/addressGrouping';

export type SuggestField = 'query' | 'postcode' | 'locality';

export interface SuggestOption {
  label: string;
  street?: string;
  postcode?: string;
  locality?: string;
}

type ExtractFn = (suggestions: GeopositSuggestion[]) => SuggestOption[];

function extractStreets(suggestions: GeopositSuggestion[]): SuggestOption[] {
  const seen = new Set<string>();
  const items: SuggestOption[] = [];
  for (const s of suggestions) {
    const street = s.street || '';
    const postcode = s.postcode || '';
    const locality = s.locality || '';
    const key = `${street}|${postcode}|${locality}`;
    if (!seen.has(key) && street) {
      seen.add(key);
      items.push({ label: street, street, postcode, locality });
    }
  }
  return items;
}

function extractPostcodes(suggestions: GeopositSuggestion[]): SuggestOption[] {
  const seen = new Set<string>();
  const items: SuggestOption[] = [];
  for (const s of suggestions) {
    const postcode = s.postcode || '';
    const locality = s.locality || '';
    const key = `${postcode}|${locality}`;
    if (!seen.has(key) && postcode) {
      seen.add(key);
      items.push({ label: `${postcode} ${locality}`.trim(), postcode, locality });
    }
  }
  return items;
}

function extractLocalities(suggestions: GeopositSuggestion[]): SuggestOption[] {
  const seen = new Set<string>();
  const items: SuggestOption[] = [];
  for (const s of suggestions) {
    const locality = s.locality || '';
    if (!seen.has(locality) && locality) {
      seen.add(locality);
      items.push({ label: locality, locality });
    }
  }
  return items;
}

const extractors: Record<SuggestField, ExtractFn> = {
  query: extractStreets,
  postcode: extractPostcodes,
  locality: extractLocalities,
};

export function useAddressSuggest(field: SuggestField, country = 'se', debounceMs = 250, disabled = false) {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<SuggestOption[]>([]);
  const [loading, setLoading] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clear suggestions when country changes
  useEffect(() => {
    setSuggestions([]);
  }, [country]);

  const fetchSuggestions = useCallback(async (searchQuery: string) => {
    if (disabled || searchQuery.length < 2) {
      setSuggestions([]);
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('query', searchQuery);
      params.set('max_rows', '50');
      params.set('type', 'suggest');
      params.set('country', country);
      // Map hook field to Geposit suggest endpoint type
      const suggestField = field === 'query' ? 'address' : field;
      params.set('suggest_field', suggestField);

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

      const response = await fetch(
        `${supabaseUrl}/functions/v1/valid-search?${params.toString()}`,
        {
          headers: {
            'apikey': supabaseKey,
            'Authorization': `Bearer ${supabaseKey}`,
          },
          signal: controller.signal,
        }
      );

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      if (response.status === 429) {
        setSuggestions([]);
        return;
      }

      const data = await response.json();
      const raw: GeopositSuggestion[] = data.suggestions || [];
      setSuggestions(extractors[field](raw).slice(0, 10));
    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      console.error('Suggest error:', err);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, [field, country, disabled]);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);

    timerRef.current = setTimeout(() => {
      fetchSuggestions(query);
    }, debounceMs);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [query, debounceMs, fetchSuggestions]);

  const clear = useCallback(() => {
    setSuggestions([]);
    setQuery('');
    abortRef.current?.abort();
  }, []);

  return { query, setQuery, suggestions, loading, clear };
}
